/**
 * kevia-autonomy/index.js
 * Unified entry point for the Autonomous Pastoral Assistant layer.
 *
 * Runs all 6 autonomy modules (all pure/fast, in-memory + localStorage)
 * and synthesizes ONE compact [autonomy: ...] context string for LLM injection.
 *
 * Design: full intelligence in computation, minimal token cost in injection.
 * The [autonomy: ...] slot carries the SINGLE most important signal — not all of them.
 * All components are returned separately for the debug bar.
 */

import { buildPastoralPriorityQueue }    from "./pastoralPriorityQueue";
import { buildFollowupRecommendations }  from "./followupRecommendationEngine";
import { assessMinistryLoad }            from "./ministryLoadBalancer";
import { buildVisitSuggestions }         from "./smartVisitPlanner";
import { detectWorkflowChains }          from "./workflowContinuationEngine";
import { analyzeMinistryRhythm }         from "./ministryRhythmAnalyzer";
import { isSessionSaturated }            from "../kevia-runtime/humanRhythm";

const MAX_AUTONOMY_CHARS = 220;

// ── Session-level dedup — don't surface the same signal twice ────────────────
const _surfacedSignals = new Set();
const SIGNAL_COOLDOWN  = 15 * 60 * 1000; // 15 min
const _signalTimestamps = new Map();

function isSignalFresh(key) {
  const last = _signalTimestamps.get(key);
  return !last || (Date.now() - last) >= SIGNAL_COOLDOWN;
}

function markSignal(key) {
  _surfacedSignals.add(key);
  _signalTimestamps.set(key, Date.now());
}

/** Reset on sign-out / session end. */
export function resetAutonomyState() {
  _surfacedSignals.clear();
  _signalTimestamps.clear();
}

/**
 * Build the full autonomy context for the current state.
 *
 * @param {{
 *   entityContext?: { id: string, type: string, name: string, tone: string } | null,
 *   mood?:          string,
 *   forceRun?:      boolean,  — bypass session saturation gate (for debug)
 * }} opts
 * @returns {{
 *   autonomyContext:  string | null,   // "[autonomy: ...]" for LLM injection
 *   debugInfo: {
 *     priorities:      number,
 *     followups:       number,
 *     load:            string,
 *     continuity:      boolean,
 *     rhythmImbalance: boolean,
 *     openProcesses:   number,
 *   }
 * }}
 */
export function buildAutonomyContext({
  entityContext = null,
  mood          = "neutral",
  forceRun      = false,
} = {}) {
  // Don't add autonomy noise during grief or saturated sessions
  if (!forceRun && (mood === "grief" || isSessionSaturated())) {
    return { autonomyContext: null, debugInfo: _emptyDebug() };
  }

  // ── Run all modules ────────────────────────────────────────────────────────
  const priorities = buildPastoralPriorityQueue();
  const followups  = buildFollowupRecommendations();
  const loadAssess = assessMinistryLoad();
  const visits     = buildVisitSuggestions();
  const chains     = detectWorkflowChains();
  const rhythm     = analyzeMinistryRhythm();

  // ── Signal selection — pick the most important, avoid repetition ──────────
  let selectedSignal = null;

  // Priority 1: critical load warning (rare, high-value)
  if (loadAssess.load === "critical" && loadAssess.balanceNote) {
    const key = "load:critical";
    if (isSignalFresh(key)) {
      selectedSignal = loadAssess.balanceNote;
      markSignal(key);
    }
  }

  // Priority 2: high-priority pastoral item from the queue
  if (!selectedSignal && priorities.hasHighPriority && priorities.compact) {
    const key = `priority:${priorities.items[0]?.label?.slice(0, 20)}`;
    if (isSignalFresh(key)) {
      selectedSignal = priorities.compact;
      markSignal(key);
    }
  }

  // Priority 3: entity-specific followup recommendation
  if (!selectedSignal && followups.compact) {
    const key = `followup:${followups.recommendations[0]?.entityName}`;
    if (isSignalFresh(key)) {
      selectedSignal = followups.compact;
      markSignal(key);
    }
  }

  // Priority 4: workflow chain continuation
  if (!selectedSignal && chains.compact) {
    const key = `chain:${chains.chains[0]?.fromType}`;
    if (isSignalFresh(key)) {
      selectedSignal = chains.compact;
      markSignal(key);
    }
  }

  // Priority 5: rhythm imbalance (lowest — only when nothing else fires)
  if (!selectedSignal && rhythm.suggestion) {
    const key = `rhythm:${rhythm.dominantActivity}`;
    if (isSignalFresh(key)) {
      selectedSignal = rhythm.suggestion;
      markSignal(key);
    }
  }

  // Priority 6: visit suggestion when entity context is present
  if (!selectedSignal && visits.compact && !entityContext) {
    const key = "visits:suggestion";
    if (isSignalFresh(key)) {
      selectedSignal = visits.compact;
      markSignal(key);
    }
  }

  const autonomyContext = selectedSignal
    ? `[autonomy: ${selectedSignal.slice(0, MAX_AUTONOMY_CHARS)}]`
    : null;

  const debugInfo = {
    priorities:      priorities.items.length,
    followups:       followups.recommendations.length,
    load:            loadAssess.load,
    continuity:      chains.chains.length > 0,
    rhythmImbalance: !!rhythm.imbalance,
    openProcesses:   chains.chains.length,
  };

  return { autonomyContext, debugInfo };
}

function _emptyDebug() {
  return {
    priorities: 0, followups: 0, load: "low",
    continuity: false, rhythmImbalance: false, openProcesses: 0,
  };
}
